import { Modal, Layout, Divider } from 'antd'
import { useDevices } from '~/hooks'
import { useEffect, useState } from 'react'
import { UAParser } from 'ua-parser-js'
import moment from 'moment'
import { Timestamp } from 'firebase/firestore'
import { Flex, Icon, Text, TextLink, colors } from '@weareberyl/design-system'

import { DEVICE_WARNING_COUNT } from '~/constants/domain'

const { Header } = Layout

interface UserAgentProps {
  userAgent: string
  timestamp: Timestamp
  deviceID: string
  currentDeviceID: string | null
}

const UserAgent = ({
  userAgent,
  timestamp,
  deviceID,
  currentDeviceID,
}: UserAgentProps) => {
  const parsedUA = new UAParser(userAgent).getResult()
  const browser = parsedUA.browser
  const device = parsedUA.device
  const isCurrentDevice = currentDeviceID && deviceID === currentDeviceID
  const timestampFormatted = moment(timestamp.toDate()).format(
    'h:mma, dddd Do MMMM  YYYY',
  )
  let deviceName = device.vendor
  if (isCurrentDevice) deviceName += ' (CURRENT)'
  return (
    <>
      <Flex flexDirection="column" style={{ gap: 5 }}>
        <Text variant="large">Device: {deviceName}</Text>
        <Text>Browser: {browser.name}</Text>
        <Text>Used at: {timestampFormatted}</Text>
      </Flex>
      <Divider />
    </>
  )
}

const DeviceWarningHeader = ({ showModal }) => {
  return (
    <Header style={{ background: colors.jaffa, height: '45px' }}>
      <Flex height="100%" alignItems="center" justifyContent="center">
        <Text mr="5">
          Warning: Logged in on multiple devices.{' '}
          <TextLink onClick={() => showModal()}>More info</TextLink>
        </Text>
      </Flex>
    </Header>
  )
}

const Devices = () => {
  const { devices, fetchDevices, loading, error, currentDeviceId } =
    useDevices()
  const [hideModal, setHideModal] = useState(true)

  const handleCancel = () => {
    setHideModal(true)
  }
  const showModal = () => {
    setHideModal(false)
  }

  // Get on initial load after app load,
  // i.e. user comes to app already logged in
  useEffect(() => {
    fetchDevices()
  }, [fetchDevices])

  const modalContent = (
    <>
      <Flex mb={20} alignItems="center" justifyContent="center" width="100%">
        <Icon type="warning" color="jaffa" height="50" width="50"></Icon>
      </Flex>
      <Text display="block" mb={20} variant="regular">
        <p>You have multiple active logins. Please logout from old sessions.</p>
      </Text>
      <Divider />
      {devices.map(device => (
        <UserAgent
          deviceID={device.deviceID}
          currentDeviceID={currentDeviceId}
          userAgent={device.userAgent}
          timestamp={device.timestamp}
          key={device.deviceID}
        ></UserAgent>
      ))}
      <Text>
        <p>
          <strong>Note:</strong> Use the logout button to remove a session from
          this list
        </p>
      </Text>
    </>
  )

  return (
    <>
      {!loading && !error && devices.length > DEVICE_WARNING_COUNT && (
        <Layout>
          <DeviceWarningHeader showModal={showModal}></DeviceWarningHeader>
        </Layout>
      )}

      <Modal
        open={
          !loading &&
          !error &&
          devices.length > DEVICE_WARNING_COUNT &&
          !hideModal
        }
        onCancel={handleCancel}
        title={<strong>Logged in on multiple devices</strong>}
        footer={''}
      >
        {modalContent}
      </Modal>
    </>
  )
}

export default Devices
